import { ApiError } from './ApiError';
import { AUTH_STRATEGY } from './client';
import { SESSION_EXPIRED_MESSAGE } from 'constants/apiErrorMessages';
import { CLIENT_STATUS, HTTP_STATUS } from 'constants/httpStatusCodes';
import { isAuthenticationFailure } from 'helpers/authenticationErrors';
import { notifySessionExpired } from 'helpers/sessionExpiredNotifier';

function resolveAuthHeaders(serviceConfig, getToken) {
  if (serviceConfig.authStrategy === AUTH_STRATEGY.SECRET_KEY) {
    return { 'secret-key': serviceConfig.secretKey };
  }

  if (serviceConfig.authStrategy === AUTH_STRATEGY.BEARER_TOKEN) {
    const token = getToken ? getToken() : null;
    if (!token) {
      throw new ApiError('Authentication required. Please log in.', {
        statusCode: HTTP_STATUS.UNAUTHORIZED,
        data: null,
        isNetworkError: false,
      });
    }

    const headers = { Authorization: `Bearer ${token}` };
    if (serviceConfig.tenant) {
      headers['x-tenant'] = serviceConfig.tenant;
    }
    return headers;
  }

  return {};
}

function buildUrl(baseUrl, path, searchParams = {}) {
  const cleanBase = baseUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const url = new URL(cleanPath, `${cleanBase}/`);

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });

  return url;
}

function extractErrorMessage(data, fallback) {
  if (data && typeof data === 'object') {
    return data.message || data.error || data.msg || fallback;
  }
  return fallback;
}

function isBusinessLogicFailure(data) {
  if (!data || typeof data !== 'object') return false;
  if ('status' in data) {
    const status = data.status;
    if (status === 'success' || status === true) return false;
    if (status === 'failure' || status === 'error' || status === false) return true;
  }
  return false;
}

export async function request(serviceConfig, path, options = {}) {
  const {
    method = 'GET',
    headers = {},
    body,
    searchParams = {},
    getToken = null,
  } = options;

  const url = buildUrl(serviceConfig.baseUrl, path, searchParams);
  const authHeaders = resolveAuthHeaders(serviceConfig, getToken);
  const fetchOptions = {
    method,
    headers: { Accept: 'application/json', ...authHeaders, ...headers },
  };

  if (body !== undefined) {
    fetchOptions.body = body;
  }

  let response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (networkError) {
    throw new ApiError(
      networkError.message || 'A network error occurred. Please check your connection.',
      {
        statusCode: CLIENT_STATUS.NO_HTTP_RESPONSE,
        data: null,
        isNetworkError: true,
      }
    );
  }

  let data;
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    if (isAuthenticationFailure(response.status, data)) {
      notifySessionExpired(SESSION_EXPIRED_MESSAGE);
      throw new ApiError(SESSION_EXPIRED_MESSAGE, {
        statusCode: response.status,
        data,
        isNetworkError: false,
      });
    }

    const message = extractErrorMessage(data, `Request failed: ${method} ${path} (${response.status})`);
    throw new ApiError(message, { statusCode: response.status, data, isNetworkError: false });
  }

  if (isBusinessLogicFailure(data)) {
    const message = extractErrorMessage(data, `Request succeeded but operation failed: ${method} ${path}`);
    throw new ApiError(message, { statusCode: response.status, data, isNetworkError: false });
  }

  return { response, data };
}
